import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { InlineTableComponent } from '@morningstar/shared/base/base-component/inline-table-component';
import { select } from '@morningstar/shared/operators';
import { MStarValidators } from '@morningstar/shared/validators';
import { merge, Subject } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';
import { MeetingAttendee } from '@morningstar/shared/meeting-attendees';
import { MeetingAttendeeSelectors } from './meeting-attendee.selectors';
import { MeetingAttendeeTableService } from './meeting-attendee-table.service';

@Component({
  selector: 'wsg-meeting-attendees',
  templateUrl: './meeting-attendees.component.html',
  styleUrls: ['./meeting-attendees.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeetingAttendeesComponent extends InlineTableComponent<MeetingAttendee> implements OnDestroy {

  // == CONSTANTS ========================================================

  useBaseGetDataMethod = false;

  columnsToDisplay = [
    'actions',
    'arrived',
    'firstName',
    'lastName',
    'keyholder',
    'email',
    'phoneNumber',
    'pSeq'
  ];

  // == STATE ============================================================

  state$ = this.meetingAttendeeService.state$;

  // == ACTIONS ==========================================================

  updatePerson = new Subject<[number, number]>(); // index, pseq
  updatePerson$ = this.updatePerson.asObservable();

  // == INTERMEDIATE =====================================================

  people$ = this.state$.pipe(select<MeetingAttendee[]>(MeetingAttendeeSelectors.peopleOptions));

  // == SIDE EFFECTS =====================================================

  onUpdatePerson$ = this.updatePerson$.pipe(
    withLatestFrom(this.people$),
    tap(([[index, pSeq], people]) => {
      const formGroup = this.formArray.at(index) as FormGroup;
      const person = people.find(p => p.pSeq === pSeq);

      if (!person) {
        return;
      }

      formGroup.patchValue({
        email: person.email,
        phoneNumber: person.phoneNumber,
        lastName: person.lastName,
        firstName: person.firstName
      });
    })
  );

  // == SUBSCRIPTION =====================================================

  concreteSubscription = merge(
    this.onUpdatePerson$
  ).subscribe();

  // == LIFECYCLE ========================================================

  constructor(protected dialog: MatDialog,
              protected formBuilder: FormBuilder,
              private matDialogRef: MatDialogRef<MeetingAttendeesComponent>,
              @Inject(MAT_DIALOG_DATA) private meetingId: number,
              protected meetingAttendeeService: MeetingAttendeeTableService) {
    super(meetingAttendeeService, dialog, formBuilder);

    this.meetingAttendeeService.getAttendees(this.meetingId);
    this.meetingAttendeeService.getAttendeePeopleOptions(this.meetingId);

    this.dataSource.data = [];
  }

  ngOnDestroy() {
    this.concreteSubscription.unsubscribe();
  }

  // == HELPERS ==========================================================

  onPersonChange(index: number, event: MatSelectChange) {
    this.updatePerson.next([index, event.value]);
  }

  addDefaultGroupToFormArray() {
    this.formArray.push(this.createFormGroup({
      id: null,
      arrived: false,
      new: true,
      meetingId: this.meetingId,
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null,
      unexpected: false,
      keyholder: false,
      pSeq: null,
      arrivalDate: null
    }));
  }

  createFormGroup(meetingAttendee: MeetingAttendee) {
    return this.formBuilder.group({
      id: meetingAttendee.id,
      new: meetingAttendee.new,
      arrived: meetingAttendee.arrived,
      meetingId: meetingAttendee.meetingId,
      firstName: [meetingAttendee.firstName, [Validators.required, Validators.maxLength(50)]],
      lastName: [meetingAttendee.lastName, [Validators.required, Validators.maxLength(50)]],
      email: [meetingAttendee.email, [Validators.required, Validators.maxLength(100), Validators.email]],
      phoneNumber: [meetingAttendee.phoneNumber, [Validators.maxLength(20), MStarValidators.phone]],
      unexpected: meetingAttendee.unexpected,
      keyholder: meetingAttendee.keyholder,
      pSeq: meetingAttendee.pSeq,
      arrivalDate: meetingAttendee.arrivalDate
    });
  }
}
